import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

// Get tomorrow's date as YYYY-MM-DD
function getTomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

function formatHumanDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

export default function Step0DeliveryType({ formData, updateFormData, onNext }) {
  const [nextDayAvailable, setNextDayAvailable] = useState(null); // null = loading
  const [nextDaySpotsLeft, setNextDaySpotsLeft] = useState(null);
  const [checking, setChecking] = useState(true);

  const tomorrow = getTomorrow();

  useEffect(() => {
    checkNextDayAvailability();
  }, []);

  async function checkNextDayAvailability() {
    setChecking(true);

    if (!isSupabaseConfigured()) {
      // Mock mode — always show next day as available with 5 spots
      setNextDayAvailable(true);
      setNextDaySpotsLeft(5);
      setChecking(false);
      return;
    }

    try {
      // 1. Get the daily cap for next_day orders
      const { data: settings } = await supabase
        .from('delivery_settings')
        .select('next_day_daily_limit')
        .single();

      const limit = settings?.next_day_daily_limit ?? 5; // default 5 if not configured

      // 2. Count confirmed orders for tomorrow with next_day type
      const { count } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('delivery_date', tomorrow)
        .eq('delivery_type', 'next_day')
        .not('status', 'eq', 'cancelled');

      const booked = count || 0;
      const spotsLeft = Math.max(0, limit - booked);

      setNextDaySpotsLeft(spotsLeft);
      setNextDayAvailable(spotsLeft > 0);
    } catch (err) {
      console.error('Availability check failed:', err);
      // Fail safe — show next day as unavailable if check fails
      setNextDayAvailable(false);
      setNextDaySpotsLeft(0);
    } finally {
      setChecking(false);
    }
  }

  const handleSelect = (type) => {
    if (type === 'next_day' && !nextDayAvailable) return;
    updateFormData({
      delivery_type: type,
      delivery_date: type === 'next_day' ? tomorrow : '',
    });
  };

  const handleContinue = () => {
    if (!formData.delivery_type) return;
    onNext();
  };

  const isNextDay = formData.delivery_type === 'next_day';
  const isScheduled = formData.delivery_type === 'scheduled';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-10"
    >
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-4xl md:text-5xl font-serif text-charcoal">When Do You Need It?</h2>
        <p className="text-sage-500 text-lg md:text-xl font-medium">
          Choose your delivery timing to get started.
        </p>
        <p className="text-sm text-sage-400">
          Returning customer?{' '}
          <a href="#/portal" className="text-blush-400 font-bold hover:text-blush-500 transition-colors underline-offset-2 hover:underline">
            Track your existing order →
          </a>
        </p>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">

        {/* Next Day Card */}
        <motion.button
          whileHover={nextDayAvailable && !checking ? { y: -4 } : {}}
          whileTap={nextDayAvailable && !checking ? { scale: 0.98 } : {}}
          onClick={() => handleSelect('next_day')}
          disabled={!nextDayAvailable || checking}
          className={`relative text-left rounded-[2.5rem] p-8 border-2 transition-all duration-300 focus:outline-none ${
            checking
              ? 'border-sage-100 bg-white opacity-60 cursor-wait'
              : !nextDayAvailable
              ? 'border-sage-100 bg-sage-50 opacity-50 cursor-not-allowed'
              : isNextDay
              ? 'border-blush-300 bg-blush-50/30 shadow-lg ring-4 ring-blush-100'
              : 'border-sage-100 bg-white hover:border-blush-200 hover:shadow-soft cursor-pointer'
          }`}
        >
          {/* Selected check */}
          {isNextDay && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute top-5 right-5 w-8 h-8 bg-blush-300 rounded-full flex items-center justify-center shadow-md"
            >
              <SafeIcon name="Check" className="text-white text-sm" />
            </motion.div>
          )}

          {/* Sold out badge */}
          {!checking && !nextDayAvailable && (
            <div className="absolute top-5 right-5 bg-sage-200 text-sage-600 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
              Sold Out
            </div>
          )}

          <div className="space-y-4">
            <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center">
              <SafeIcon name="Zap" className="text-amber-400 text-2xl" />
            </div>

            <div>
              <h3 className="font-serif text-2xl text-charcoal">Next Day</h3>
              <p className="text-sage-500 text-sm mt-1 font-medium">
                Order today, delivered tomorrow
              </p>
            </div>

            {checking ? (
              <div className="flex items-center gap-2 text-sage-400 text-sm">
                <div className="w-3 h-3 border-2 border-sage-300 border-t-transparent rounded-full animate-spin" />
                Checking availability…
              </div>
            ) : nextDayAvailable ? (
              <div className="space-y-2">
                <div className="bg-amber-50 border border-amber-100 rounded-2xl px-4 py-2.5">
                  <p className="text-amber-700 font-bold text-sm">
                    {formatHumanDate(tomorrow)}
                  </p>
                </div>
                {nextDaySpotsLeft !== null && nextDaySpotsLeft <= 3 && (
                  <p className="text-amber-600 text-xs font-bold uppercase tracking-wider">
                    ⚡ Only {nextDaySpotsLeft} spot{nextDaySpotsLeft === 1 ? '' : 's'} left!
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sage-400 text-xs font-bold uppercase tracking-wider">
                No spots available for tomorrow
              </p>
            )}

            <p className="text-[10px] text-sage-400 uppercase tracking-widest font-bold">
              Select next-day items only
            </p>
          </div>
        </motion.button>

        {/* Schedule It Card */}
        <motion.button
          whileHover={{ y: -4 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleSelect('scheduled')}
          className={`relative text-left rounded-[2.5rem] p-8 border-2 transition-all duration-300 focus:outline-none ${
            isScheduled
              ? 'border-blush-300 bg-blush-50/30 shadow-lg ring-4 ring-blush-100'
              : 'border-sage-100 bg-white hover:border-blush-200 hover:shadow-soft cursor-pointer'
          }`}
        >
          {isScheduled && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute top-5 right-5 w-8 h-8 bg-blush-300 rounded-full flex items-center justify-center shadow-md"
            >
              <SafeIcon name="Check" className="text-white text-sm" />
            </motion.div>
          )}

          <div className="space-y-4">
            <div className="w-14 h-14 bg-blush-50 rounded-2xl flex items-center justify-center">
              <SafeIcon name="Calendar" className="text-blush-300 text-2xl" />
            </div>

            <div>
              <h3 className="font-serif text-2xl text-charcoal">Schedule It</h3>
              <p className="text-sage-500 text-sm mt-1 font-medium">
                Pick any date, 5+ days out
              </p>
            </div>

            <div className="bg-blush-50 border border-blush-100 rounded-2xl px-4 py-2.5">
              <p className="text-blush-500 font-bold text-sm">Full catalog available</p>
            </div>

            <p className="text-[10px] text-sage-400 uppercase tracking-widest font-bold">
              All arrangements &amp; add-ons
            </p>
          </div>
        </motion.button>
      </div>

      {/* Continue button */}
      <div className="flex justify-center pt-2">
        <motion.button
          onClick={handleContinue}
          disabled={!formData.delivery_type}
          whileHover={formData.delivery_type ? { scale: 1.02 } : {}}
          whileTap={formData.delivery_type ? { scale: 0.98 } : {}}
          className="btn-primary w-full max-w-sm md:w-auto md:px-16 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue <SafeIcon name="ArrowRight" />
        </motion.button>
      </div>
    </motion.div>
  );
}
